import { Text, View } from "@react-pdf/renderer";
import type { ReactElement } from "react";
import type { ResumeDocument } from "@/types/cv";
import { formatDateRange } from "./utils";
import { INLINE_SEPARATOR, type PdfStyles } from "./pdf-theme";

// Vertical space (pt) that must remain on the page for a block to start there,
// otherwise react-pdf pushes it to the next page. Body text is 10.5pt at 1.4
// line height (~14.7pt per line), so these are roughly 2 and 3 lines: enough to
// keep an entry header with its first bullet, and a section heading with its
// first entry.
const ENTRY_HEADER_KEEP_AHEAD = 30;
export const SECTION_HEADING_KEEP_AHEAD = 45;

// Mirrors TimelineSectionEntry in docx-builder.ts so the PDF preview shows the
// same content as the DOCX download.
export type TimelineEntry = {
  title: string;
  originalTitle?: string;
  entityName: string;
  metaParts?: string[];
  startDate: string | null;
  endDate: string | null;
  isCurrent?: boolean;
  detail?: string;
  points?: string[];
};

export const resolveDisplayedTitle = (title: string, rewriteTitle?: string) => {
  const originalTitle = title.trim();
  const rewrittenTitle = rewriteTitle?.trim();
  const displayTitle =
    rewrittenTitle && rewrittenTitle.length > 0 ? rewrittenTitle : originalTitle;

  return {
    displayTitle,
    originalTitle:
      rewrittenTitle && rewrittenTitle !== originalTitle
        ? originalTitle
        : undefined,
  };
};

// Matches createMultilineParagraph in docx-builder.ts: blank lines collapse,
// remaining lines are trimmed, and line breaks are preserved in the text run.
export const collapseMultiline = (content: string) =>
  content
    .split(/\n+/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join("\n");

const trimPoints = (points: string[] | undefined) =>
  (points ?? [])
    .map((point) => point.trim())
    .filter((point) => point.length > 0);

// wrap={false} keeps the dot with its text: a bullet that straddles a page break
// otherwise leaves its marker stranded at the bottom of the previous page.
// Bullets are always far shorter than a page, so they cannot be clipped by this.
export const BulletRow = ({
  styles,
  text,
}: {
  styles: PdfStyles;
  text: string;
}) => (
  <View style={styles.bulletRow} wrap={false}>
    <Text style={styles.bulletDot}>•</Text>
    <Text style={styles.bulletText}>{text}</Text>
  </View>
);

export const TimelineEntryView = ({
  styles,
  entry,
}: {
  styles: PdfStyles;
  entry: TimelineEntry;
}) => {
  const displayTitle = entry.title.trim();
  const period = formatDateRange(
    entry.startDate,
    entry.isCurrent ? null : entry.endDate,
    { canBeCurrent: Boolean(entry.isCurrent) },
  );
  const originalTitle = entry.originalTitle?.trim();
  const entityName = entry.entityName.trim();
  const metaParts = (entry.metaParts ?? [])
    .map((part) => part.trim())
    .filter(Boolean);
  const points = trimPoints(entry.points);
  const detail =
    points.length === 0 && entry.detail ? collapseMultiline(entry.detail) : "";

  if (
    !displayTitle &&
    !originalTitle &&
    !entityName &&
    metaParts.length === 0 &&
    !detail &&
    points.length === 0
  ) {
    return null;
  }

  return (
    <View>
      {/* Title, period, and employer stay on one page together. Detail text and
          bullets flow normally so a long entry is never clipped, which is what
          wrapping the whole entry in wrap={false} would do. */}
      <View wrap={false} minPresenceAhead={ENTRY_HEADER_KEEP_AHEAD}>
        {displayTitle ? (
          <View style={styles.entryHeader}>
            <Text style={styles.entryTitle}>{displayTitle}</Text>
            {period ? <Text style={styles.entryPeriod}>{period}</Text> : null}
          </View>
        ) : null}

        {originalTitle ? (
          <Text style={styles.originalTitle}>Original: {originalTitle}</Text>
        ) : null}

        {entityName || metaParts.length > 0 ? (
          <Text style={styles.entityLine}>
            {entityName ? (
              <Text style={styles.entityName}>{entityName}</Text>
            ) : null}
            {metaParts.length > 0 ? (
              <Text style={styles.entityMeta}>
                {entityName ? INLINE_SEPARATOR : ""}
                {metaParts.join(INLINE_SEPARATOR)}
              </Text>
            ) : null}
          </Text>
        ) : null}
      </View>

      {detail ? (
        <Text style={styles.detailText} orphans={2} widows={2}>
          {detail}
        </Text>
      ) : null}

      {points.map((point, index) => (
        <BulletRow key={index} styles={styles} text={point} />
      ))}
    </View>
  );
};

export const EducationSection = ({
  styles,
  resume,
  sectionTitle,
}: {
  styles: PdfStyles;
  resume: ResumeDocument;
  sectionTitle: ReactElement | string;
}) => {
  if (resume.education.length === 0) return null;

  return (
    <View>
      {sectionTitle}
      {resume.education.map((education, index) => {
        const period = formatDateRange(education.start_date, education.end_date);
        const institution = education.institution.trim();
        const { displayTitle: programTitle, originalTitle } =
          resolveDisplayedTitle(
            education.program_title,
            education.rewrite_program_title,
          );
        const academicDetails = [education.degree.trim(), education.major.trim()]
          .filter((value) => value.length > 0)
          .join(INLINE_SEPARATOR);
        const gpa = education.gpa.trim();
        const withGpa = gpa
          ? `${academicDetails}${academicDetails ? INLINE_SEPARATOR : ""}GPA ${gpa}`
          : academicDetails;
        const points = trimPoints(education.points);

        return (
          <View key={index}>
            <View wrap={false} minPresenceAhead={ENTRY_HEADER_KEEP_AHEAD}>
              {institution ? (
                <View style={styles.entryHeader}>
                  <Text style={styles.educationInstitution}>{institution}</Text>
                  {period ? (
                    <Text style={styles.entryPeriod}>{period}</Text>
                  ) : null}
                </View>
              ) : null}

              {programTitle ? (
                <Text style={styles.programTitle}>{programTitle}</Text>
              ) : null}

              {originalTitle ? (
                <Text style={styles.originalTitle}>
                  Original: {originalTitle}
                </Text>
              ) : null}

              {withGpa ? <Text style={styles.entityMeta}>{withGpa}</Text> : null}
            </View>

            {points.map((point, pointIndex) => (
              <BulletRow key={pointIndex} styles={styles} text={point} />
            ))}
          </View>
        );
      })}
    </View>
  );
};
